import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { DocumentUnderstandingAgentRelationshipOverride } from 'services/repositories/documentUnderstandingAgent/documentUnderstandingAgent';
import { SlideOver } from 'components/Display/SlideOver/SlideOver';
import { PartnerLogo } from 'components/PartnerLogo/PartnerLogo';

import { PRICE_STRATEGY_LABEL_KEYS } from '../../../helpers';
import { PreferredUnitItem } from '../../PreferredUnitItem/PreferredUnitItem';
import { GlobalValue } from './GlobalValue/GlobalValue';

type ConnectionDrawerProps = {
  override: DocumentUnderstandingAgentRelationshipOverride | null;
  onClose: () => void;
};

export const ConnectionDrawer = ({ override, onClose }: ConnectionDrawerProps) => {
  const { t } = useTranslation();
  const lastOverrideRef = useRef(override);

  if (override) {
    lastOverrideRef.current = override;
  }
  // Keeps the drawer content in place while the close transition plays instead of flashing empty.
  const displayedOverride = override ?? lastOverrideRef.current;

  return (
    <SlideOver
      open={override !== null}
      setOpen={onClose}
      headerImage={
        <PartnerLogo
          imageUrl={displayedOverride?.counterparty.logo_url}
          name={displayedOverride?.counterparty.name}
          size="large"
        />
      }
      title={displayedOverride?.counterparty.name}
    >
      {displayedOverride ? (
        <div className="space-y-8" data-testid="connection-drawer-content">
          <SlideOver.Section title={t('settings:documentUnderstandingAgent.global.priceStrategy.title')}>
            {displayedOverride.configuration.prices_extraction_strategy ? (
              t(PRICE_STRATEGY_LABEL_KEYS[displayedOverride.configuration.prices_extraction_strategy])
            ) : (
              <GlobalValue />
            )}
          </SlideOver.Section>

          <SlideOver.Section title={t('settings:documentUnderstandingAgent.global.unitsOfMeasure.title')}>
            {displayedOverride.configuration.preferred_units_of_measure.length > 0 ? (
              <ol className="flex flex-col gap-2">
                {displayedOverride.configuration.preferred_units_of_measure.map((unit, index) => (
                  <PreferredUnitItem key={`${unit}-${index}`} order={index + 1} unitOfMeasure={unit} />
                ))}
              </ol>
            ) : (
              <GlobalValue />
            )}
          </SlideOver.Section>

          <SlideOver.Section title={t('settings:documentUnderstandingAgent.global.customInstructions.title')}>
            {displayedOverride.configuration.custom_instructions ? (
              <p className="whitespace-pre-wrap">{displayedOverride.configuration.custom_instructions}</p>
            ) : (
              <GlobalValue />
            )}
          </SlideOver.Section>

          {displayedOverride.configuration.custom_line_item_quantity_types.length > 0 ? (
            <SlideOver.Section
              title={t('settings:documentUnderstandingAgent.byConnections.drawer.customLineItemQuantityTypes.title')}
            >
              <ul className="flex flex-col gap-1">
                {displayedOverride.configuration.custom_line_item_quantity_types.map((type, index) => (
                  <li key={`${type.value}-${index}`}>
                    {type.value} — {type.description}
                  </li>
                ))}
              </ul>
            </SlideOver.Section>
          ) : null}
        </div>
      ) : null}
    </SlideOver>
  );
};

/**
 * i18n keys
 * t('settings:documentUnderstandingAgent.global.priceStrategy.strategy.disabled')
 * t('settings:documentUnderstandingAgent.global.priceStrategy.strategy.document')
 * t('settings:documentUnderstandingAgent.global.priceStrategy.strategy.documentAndMasterData')
 * t('settings:documentUnderstandingAgent.global.priceStrategy.strategy.masterData')
 */
